import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        // Default: Background --primary, Text --cream-light, Border --accent-gold
        default: 'bg-[var(--primary)] text-[var(--cream-light)] hover:opacity-90 shadow-[var(--shadow-button)] border border-[var(--accent-gold)] min-h-[44px] hover:shadow-[var(--shadow-button-hover)] hover:-translate-y-[1px]',
        // Secondary: Background --card-bg, Text --text-primary, Border --accent-gold, hovers to primary
        secondary: 'bg-[var(--card-bg)] text-[var(--text-primary)] hover:bg-[var(--primary)] hover:text-[var(--cream-light)] border border-[var(--accent-gold)] min-h-[44px] hover:shadow-[var(--shadow-button)]',
        // Outline: Transparent background, Text --accent-gold (bronze is visible on both light/dark), Border 2px --accent-gold
        outline: 'border-2 border-[var(--accent-gold)] bg-transparent text-[var(--accent-gold)] shadow-[var(--shadow-sm)] hover:bg-[var(--accent-gold)]/10 hover:shadow-[var(--shadow-button)] min-h-[44px]',
        // Ghost: Transparent background, Text --accent-gold (bronze is visible on both light/dark)
        ghost: 'bg-transparent text-[var(--accent-gold)] hover:bg-[var(--accent-gold)]/10 min-h-[44px]',
        // Destructive: Background --error, Text white, Border --accent-gold
        destructive: 'bg-[var(--error)] text-white hover:opacity-90 border border-[var(--accent-gold)] shadow-[var(--shadow-button)] min-h-[44px] hover:shadow-[var(--shadow-button-hover)]',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, style, disabled, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        style={style}
        disabled={disabled}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }

